import { onWebViewModalClose, onWebViewModalOpen } from '@fourthwall/utils'
import React, { type ReactNode, useEffect } from 'react'

import { Popper } from '../Popper'
import { DropdownProvider, useDropdown } from './DropdownContext'
import { DropdownDivider } from './DropdownDivider/DropdownDivider'
import { DropdownHeading } from './DropdownHeading/DropdownHeading'
import { DropdownItem } from './DropdownItem/DropdownItem'

import * as S from './styled'

export interface DropdownProps {
  content: ReactNode
  footer?: ReactNode
  isOpen?: boolean
  strategy?: 'absolute' | 'fixed' | undefined
  position?:
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'right-start'
    | 'right-end'
    | 'left-start'
    | 'left-end'
  fluid?: boolean
  maxHeight?: number
  maxWidth?: number | string
  optionsVerticalOffset?: number
  optionsHorizontalOffset?: number
  testid?: string
  children: ReactNode
  onClose?: () => void
}

export const Dropdown = (props: DropdownProps) => (
  <DropdownProvider>
    <DropdownContent {...props} />
  </DropdownProvider>
)

export const DropdownContent = ({
  isOpen: isOpenProp,
  strategy = undefined,
  content,
  footer,
  position = 'bottom-start',
  optionsVerticalOffset = 8,
  optionsHorizontalOffset = 0,
  fluid = false,
  maxHeight,
  maxWidth = 240,
  testid = 'Dropdown',
  children,
  onClose,
}: DropdownProps) => {
  const { isOpen, close, toggle } = useDropdown()

  const isExternallyControlled = isOpenProp !== undefined

  useEffect(() => {
    if (!isExternallyControlled) return

    if (isOpenProp) {
      onWebViewModalOpen()
    } else {
      onWebViewModalClose()
    }
  }, [isExternallyControlled, isOpenProp])

  return (
    <Popper
      isOpen={isExternallyControlled ? isOpenProp : isOpen}
      referenceComponent={
        <S.Container data-testid={testid}>
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              return React.cloneElement<any>(child, {
                onClick: (event: MouseEvent) => {
                  child.props.onClick?.(event)
                  !isExternallyControlled && toggle()
                },
              })
            }
          })}
        </S.Container>
      }
      popperProps={{
        placement: position,
        strategy,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [optionsHorizontalOffset, optionsVerticalOffset],
            },
          },
        ],
      }}
      onClose={() => {
        onClose?.()
        close()
      }}
    >
      <S.Dropdown role="menu" $fluid={fluid} $maxWidth={maxWidth}>
        <S.Options $maxHeight={maxHeight}>{content}</S.Options>
        {footer}
      </S.Dropdown>
    </Popper>
  )
}

Dropdown.Item = DropdownItem
Dropdown.Divider = DropdownDivider
Dropdown.Heading = DropdownHeading
